// Planner event card — design direction D «Warm Precision», spec screen
// «Календарь» rich rows: two lines per card.
//   line 1: mono time · client name (who the event is about) · amount right;
//   line 2: the service/subject, quiet secondary.
// The type reads as a 2px colored edge (family tone) with a native tooltip;
// tasks keep their checkbox, deadlines stay outlines. Overdue stays quiet
// (owner review): error-colored edge + a small icon by the time — no pill.
//
// Accessibility stays the same as the compact chip: ONE stretched opener
// button per card (no nested interactives); the task checkbox floats above
// it with pointer-events, so a card contains at most two buttons, never
// nested. Type edge + tooltip, min-height 44px (touch target), radius S6.

import { AlertCircle, Check } from 'lucide-react';
import { useTranslation } from '@govard/i18n-core/react';
import type { CSSProperties, MouseEvent, ReactElement } from 'react';

import { useTheme, type ActualTheme } from '../../../theme/ThemeContext.js';
import {
  EVENT_ACCENT_COLOR,
  EVENT_TYPE_SURFACE,
  type PlannerEvent,
  type PlannerEventType,
} from '../lib/plannerEvents.js';

interface PlannerEventCardProps {
  event: PlannerEvent;
  onOpen: (event: PlannerEvent) => void;
  onToggleTask?: ((event: PlannerEvent) => void) | undefined;
}

function themeKey(actualTheme: ActualTheme): 'light' | 'dark' {
  return actualTheme === 'dark' ? 'dark' : 'light';
}

/** Tooltip text: what kind of record this is (native title attribute). */
const TYPE_TOOLTIP_KEY: Record<PlannerEventType, string> = {
  task: 'crm.calendar.filter.task',
  meeting: 'crm.calendar.filter.meeting',
  call: 'crm.calendar.filter.task',
  tg: 'crm.calendar.filter.tg',
  deadline: 'crm.calendar.filter.deadline',
  generic: 'crm.calendar.filter.generic',
};

/** Task checkbox — shared by the card and the list row. */
export function PlannerTaskCheckbox({
  event,
  onToggleTask,
}: {
  event: PlannerEvent;
  onToggleTask?: ((event: PlannerEvent) => void) | undefined;
}): ReactElement {
  const { t } = useTranslation();
  const isDone = event.done;
  const handleToggle = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    onToggleTask?.(event);
  };
  return (
    <button
      type="button"
      onClick={handleToggle}
      title={isDone ? t('crm.calendar.quick_status_todo') : t('crm.calendar.quick_status_done')}
      aria-label={
        isDone ? t('crm.calendar.quick_status_todo') : t('crm.calendar.quick_status_done')
      }
      className="flex h-[14px] w-[14px] shrink-0 items-center justify-center rounded-[4px] border-[1.5px] transition-colors duration-[110ms]"
      style={
        isDone
          ? {
              borderColor: 'var(--ant-color-primary)',
              backgroundColor: 'var(--ant-color-primary)',
              color: 'var(--crm-on-accent, #ffffff)',
            }
          : {
              borderColor: 'var(--ant-color-border)',
              backgroundColor: 'transparent',
              color: 'transparent',
            }
      }
    >
      <Check size={10} strokeWidth={3.5} />
    </button>
  );
}

export function PlannerEventCard({
  event,
  onOpen,
  onToggleTask,
}: PlannerEventCardProps): ReactElement {
  const { t } = useTranslation();
  const { actualTheme } = useTheme();
  const mode = themeKey(actualTheme);

  const surface = EVENT_TYPE_SURFACE[event.eventType][mode];
  const edgeColor = EVENT_ACCENT_COLOR[event.eventType][mode];
  const isDeadline = event.eventType === 'deadline';
  const isDone = event.done;
  // Overdue stays quiet (owner review): no red pill, no inverted surface —
  // only the error-colored left edge plus a small icon by the time.
  const isFlagged = event.overdue && !isDone;

  // Rich rows: line 1 prefers the person (client/contact), line 2 is the
  // service. Records without a person keep the service on line 1.
  const line1 = event.whoLabel ?? event.title;
  const line2 = event.whoLabel !== null ? event.title : (event.entityLabel ?? null);

  // The border color lives in a custom property (not an inline value) so
  // the `hover:border-current` utility can win the cascade on hover.
  const cardVars = {
    color: surface.text,
    backgroundColor: surface.bg === 'transparent' ? undefined : surface.bg,
    '--ev-border': surface.border,
    // Overdue accent is the edge only — calm card, visible signal.
    '--ev-edge': isFlagged ? 'var(--color-error, #b91c1c)' : edgeColor,
  } as CSSProperties;

  const tooltip = `${t(TYPE_TOOLTIP_KEY[event.eventType])}${
    event.timeLabel ? ` · ${event.timeLabel}` : ''
  }${isFlagged ? ` · ${t('crm.calendar.overdue')}` : ''} — ${event.title}`;

  return (
    <article
      title={tooltip}
      className={`relative flex w-full min-w-0 flex-col gap-0.5 rounded-[6px] border border-l-[2px] px-2 py-1.5 text-left transition-[border-color,background-color,opacity] duration-[110ms] border-[color:var(--ev-border)] border-l-[color:var(--ev-edge)] ${
        isDone ? 'opacity-60' : ''
      } ${
        isDeadline
          ? 'hover:bg-[color-mix(in_srgb,currentColor_8%,transparent)]'
          : 'hover:border-current'
      }`}
      style={{ ...cardVars, minHeight: 44 }}
    >
      {/* Stretched opener: one interactive element per card (axe-safe — the
          task checkbox sits on top with pointer-events). */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onOpen(event);
        }}
        aria-label={`${event.timeLabel ? `${event.timeLabel} · ` : ''}${line1}`}
        className="absolute inset-0 z-0 cursor-pointer rounded-[6px] border-0 bg-transparent outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-[color:var(--ant-color-primary-border-hover)] focus-visible:outline-offset-1"
      />
      <div className="pointer-events-none relative z-[1] flex min-w-0 flex-col gap-0.5">
        {/* Line 1: checkbox · time · overdue icon · person · amount.
            Everything sits on ONE line: flex-none markers + truncated label,
            no wrapping (owner review: icon and text never split rows). */}
        <div className="flex min-w-0 items-baseline gap-1.5 whitespace-nowrap">
          {event.eventType === 'task' ? (
            <span className="pointer-events-auto flex shrink-0 self-center">
              <PlannerTaskCheckbox event={event} onToggleTask={onToggleTask} />
            </span>
          ) : null}
          {event.timeLabel ? (
            <span
              className="flex-none font-mono text-[10.5px] tabular-nums"
              style={{ color: 'var(--ant-color-text-secondary)' }}
            >
              {event.timeLabel}
            </span>
          ) : null}
          {isFlagged ? (
            <span
              role="img"
              aria-label={t('crm.calendar.overdue')}
              title={t('crm.calendar.overdue')}
              className="flex flex-none self-center"
            >
              <AlertCircle
                size={12}
                strokeWidth={2.25}
                style={{ color: 'var(--color-error, #b91c1c)' }}
              />
            </span>
          ) : null}
          <span
            className={`min-w-0 flex-1 truncate text-[12px] font-semibold leading-4 ${isDone ? 'line-through' : ''}`}
            style={isDone ? { color: 'var(--ant-color-text-tertiary)' } : undefined}
          >
            {line1}
          </span>
          {event.amountLabel ? (
            <span
              className="ml-1 flex-none font-mono text-[11.5px] font-semibold tabular-nums"
              style={{ color: surface.text }}
            >
              {event.amountLabel}
            </span>
          ) : null}
        </div>

        {/* Line 2: the service/subject, quiet secondary. */}
        {line2 ? (
          <div
            className={`truncate text-[10.5px] leading-4 ${isDone ? 'line-through' : ''}`}
            style={{ color: 'var(--ant-color-text-secondary)' }}
          >
            {line2}
          </div>
        ) : null}
      </div>
    </article>
  );
}
